class LoftyProductForm {
  constructor(section) {
    this.section = section;
    this.productJson = section.querySelector('[data-product-json]');
    if (!this.productJson) return;
    this.product = JSON.parse(this.productJson.textContent);
    const meta = section.querySelector('[data-variant-meta]');
    this.variantMeta = meta ? JSON.parse(meta.textContent) : {};
    this.form = section.querySelector('[data-product-form]');
    this.select = section.querySelector('[data-variant-select]');
    this.addButton = section.querySelector('[data-add-to-cart]');
    this.priceEl = section.querySelector('[data-product-price]');
    this.inventoryEl = section.querySelector('[data-inventory-message]');
    this.backInStock = section.querySelector('[data-back-in-stock]');
    this.dynamicCheckout = section.querySelector('[data-dynamic-checkout]');
    const selected = this.getSelectedVariant();
    this.selectedOptions = selected && selected.options ? selected.options.slice() : [];
    this.bindOptions();
    this.bindGallery();
    this.bindSticky();
    this.update(selected);
  }
  getSelectedVariant() {
    const id = this.select ? Number(this.select.value) : null;
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const targetId = id || Number(urlVariant);
    return this.product.variants.find((v) => Number(v.id) === targetId) || this.product.variants.find((v) => v.available) || this.product.variants[0];
  }
  bindOptions() {
    this.section.querySelectorAll('[data-option-value]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.optionPosition) - 1;
        this.selectedOptions[index] = button.dataset.optionValue;
        this.update();
      });
    });
    if (this.select) {
      this.select.addEventListener('change', () => {
        const variant = this.getSelectedVariant();
        this.selectedOptions = variant.options.slice();
        this.update(variant);
      });
    }
  }
  bindGallery() {
    this.section.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.galleryThumb));
    });
  }
  bindSticky() {
    const sticky = this.section.querySelector('[data-sticky-atc]');
    const stickyButton = this.section.querySelector('[data-sticky-add]');
    if (stickyButton) {
      stickyButton.addEventListener('click', () => {
        if (stickyButton.dataset.stickyAction === 'notify') {
          this.focusRestock();
          return;
        }
        if (this.addButton && !this.addButton.disabled) this.addButton.click();
      });
    }
    if (!sticky || !this.addButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const visible = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
      });
    }, { threshold: 0 });
    observer.observe(this.addButton);
  }
  findVariant() {
    return this.product.variants.find((variant) => variant.options.every((value, index) => value === this.selectedOptions[index]));
  }
  update(variant) {
    const selected = variant || this.findVariant() || this.getSelectedVariant();
    if (!selected) return;
    const meta = this.variantMeta[String(selected.id)] || selected;
    if (this.select) this.select.value = selected.id;
    this.section.querySelectorAll('[data-option-value]').forEach((button) => {
      const index = Number(button.dataset.optionPosition) - 1;
      const isSelected = selected.options[index] === button.dataset.optionValue;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
    if (this.priceEl && meta.price_html) this.priceEl.innerHTML = meta.price_html;
    if (this.addButton) {
      this.addButton.disabled = !selected.available;
      this.addButton.textContent = selected.available ? 'Add to cart' : 'Sold out';
    }
    if (this.dynamicCheckout) this.dynamicCheckout.hidden = !selected.available;
    if (this.backInStock) this.backInStock.hidden = !!selected.available;
    if (this.inventoryEl) {
      this.inventoryEl.classList.toggle('is-soldout', !selected.available);
      const qty = Number(meta.inventory_quantity || 0);
      const threshold = Number(meta.low_stock_threshold || this.section.dataset.lowStockThreshold || 3);
      const low = selected.available && qty > 0 && qty <= threshold;
      this.inventoryEl.classList.toggle('is-low', low);
      this.inventoryEl.textContent = !selected.available ? 'This size is sold out. Join the restock list and we can track demand by size.' : low ? (qty === 1 ? 'Only 1 left in this size.' : `Only ${qty} left in this size.`) : 'In stock. Choose your size and get ready for the round.';
    }
    const stickyVariant = this.section.querySelector('[data-sticky-variant]');
    const stickyPrice = this.section.querySelector('[data-sticky-price]');
    const stickyButton = this.section.querySelector('[data-sticky-add]');
    if (stickyVariant) stickyVariant.textContent = selected.title;
    if (stickyPrice) stickyPrice.innerHTML = meta.price_html ? meta.price_html.replace(/<[^>]+>/g, '').trim() : this.formatMoney(selected.price);
    if (stickyButton) {
      stickyButton.disabled = false;
      stickyButton.dataset.stickyAction = selected.available ? 'add' : 'notify';
      stickyButton.textContent = selected.available ? 'Add to cart' : 'Notify me';
    }
    this.updateRestockFields(meta, selected);
    const mediaId = meta.featured_media_id || (selected.featured_media && selected.featured_media.id ? selected.featured_media.id : null);
    if (mediaId) this.showMedia(String(mediaId));
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', selected.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
  updateRestockFields(meta, selected) {
    const tags = this.section.querySelector('[data-restock-tags]');
    const note = this.section.querySelector('[data-restock-note]');
    if (tags && meta.restock_tags) tags.value = meta.restock_tags;
    if (note && meta.restock_note) note.value = meta.restock_note;
  }
  focusRestock() {
    if (!this.backInStock) return;
    this.backInStock.hidden = false;
    this.backInStock.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    const email = this.backInStock.querySelector('input[type="email"]');
    setTimeout(() => (email || this.backInStock).focus({ preventScroll: true }), 250);
  }
  showMedia(id) {
    this.section.querySelectorAll('[data-media-id]').forEach((item) => item.classList.toggle('is-active', String(item.dataset.mediaId) === String(id)));
    this.section.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => thumb.setAttribute('aria-current', String(thumb.dataset.galleryThumb) === String(id) ? 'true' : 'false'));
  }
  formatMoney(cents) {
    const format = this.section.dataset.moneyFormat || '${{amount}}';
    const value = (Number(cents || 0) / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, value).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(cents || 0) / 100));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-section]').forEach((section) => new LoftyProductForm(section));
});
